import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Alert,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DocIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import apiService from '../../services/api';

function ReferenceDocEditor({ reviewTypeId, documents = [], onUpdate }) {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [expandedDocs, setExpandedDocs] = useState({});
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    filename: '',
    description: '',
    content: '',
    includeInPrompt: true
  });

  const handleCreate = () => {
    setSelectedDoc(null);
    setFormData({
      name: '',
      filename: '',
      description: '',
      content: '',
      includeInPrompt: true
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (doc) => {
    setSelectedDoc(doc);
    setFormData({
      name: doc.name || '',
      filename: doc.filename || '',
      description: doc.description || '',
      content: doc.content || '',
      includeInPrompt: doc.includeInPrompt !== false
    });
    setEditDialogOpen(true);
  };

  const handleView = (doc) => {
    setSelectedDoc(doc);
    setViewDialogOpen(true);
  };

  const toggleExpand = (docId) => {
    setExpandedDocs(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      if (selectedDoc) {
        // Update existing document
        await apiService.put(
          `/review-types/${reviewTypeId}/reference-documents/${selectedDoc._id}`,
          formData
        );
        setSuccess('Document updated successfully');
      } else {
        // Create new document
        await apiService.post(
          `/review-types/${reviewTypeId}/reference-documents`,
          formData
        );
        setSuccess('Document added successfully');
      }

      setEditDialogOpen(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await apiService.delete(`/review-types/${reviewTypeId}/reference-documents/${doc._id}`);
      setSuccess('Document deleted successfully');
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleInclude = async (doc) => {
    try {
      await apiService.put(
        `/review-types/${reviewTypeId}/reference-documents/${doc._id}`,
        { includeInPrompt: !doc.includeInPrompt }
      );
      setSuccess(`Document ${doc.includeInPrompt ? 'excluded from' : 'included in'} prompts`);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update document');
    }
  };

  const getContentPreview = (content, maxLength = 150) => {
    if (!content) return 'No content';
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
          Reference Documents ({documents.length})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          size="small"
        >
          Add Document
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reference documents provide domain knowledge to the LLM during reviews. Add markdown files
        with clinical guidelines, patterns to detect, or other relevant documentation.
      </Typography>

      {documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
          <DocIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No reference documents yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add documents to provide domain knowledge for this review type
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
            Add First Document
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {documents.map((doc) => (
            <Card key={doc._id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <DocIcon color="action" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {doc.name}
                      </Typography>
                      <Chip
                        label={doc.includeInPrompt ? 'Included' : 'Excluded'}
                        size="small"
                        color={doc.includeInPrompt ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {doc.filename}
                    </Typography>
                    {doc.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {doc.description}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Collapse in={expandedDocs[doc._id]}>
                  <Paper sx={{ p: 2, mt: 2, bgcolor: '#f9f9f9', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {doc.content}
                    </Typography>
                  </Paper>
                </Collapse>

                {!expandedDocs[doc._id] && doc.content && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {getContentPreview(doc.content)}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={expandedDocs[doc._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => toggleExpand(doc._id)}
                >
                  {expandedDocs[doc._id] ? 'Collapse' : 'Expand'}
                </Button>
                <Button size="small" startIcon={<ViewIcon />} onClick={() => handleView(doc)}>
                  View
                </Button>
                <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(doc)}>
                  Edit
                </Button>
                <Button
                  size="small"
                  onClick={() => handleToggleInclude(doc)}
                  color={doc.includeInPrompt ? 'warning' : 'success'}
                >
                  {doc.includeInPrompt ? 'Exclude' : 'Include'}
                </Button>
                <Button size="small" startIcon={<DeleteIcon />} onClick={() => handleDelete(doc)} color="error">
                  Delete
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedDoc ? `Edit Document: ${selectedDoc.name}` : 'Add Reference Document'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Document Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Goal Acceptance Flow"
              required
            />

            <TextField
              fullWidth
              label="Filename"
              value={formData.filename}
              onChange={(e) => setFormData({ ...formData, filename: e.target.value })}
              placeholder="e.g., goal_acceptance_flow.md"
              helperText="A reference filename for organization"
              required
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of what this document contains"
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Content (Markdown)"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Enter the document content in Markdown format..."
              multiline
              rows={15}
              required
              sx={{ fontFamily: 'monospace' }}
              InputProps={{
                sx: { fontFamily: 'monospace' }
              }}
            />

            <Typography variant="caption" color="text.secondary">
              Tip: Use Markdown formatting for structure. Include patterns, definitions, examples,
              and specific instructions that will help the LLM understand the domain context.
            </Typography>

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.includeInPrompt}
                  onChange={(e) => setFormData({ ...formData, includeInPrompt: e.target.checked })}
                />
              }
              label="Include in LLM Prompts"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              When enabled, this document's content will be included in the system prompt for reviews of this type.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={loading || !formData.name || !formData.filename || !formData.content}
          >
            {loading ? 'Saving...' : (selectedDoc ? 'Save Changes' : 'Add Document')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocIcon />
            {selectedDoc?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDoc && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {selectedDoc.filename}
              </Typography>

              {selectedDoc.description && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Description:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedDoc.description}
                  </Typography>
                </>
              )}

              <Box sx={{ display: 'flex', gap: 1, my: 2 }}>
                <Chip
                  label={selectedDoc.includeInPrompt ? 'Included in Prompts' : 'Excluded from Prompts'}
                  color={selectedDoc.includeInPrompt ? 'success' : 'default'}
                  size="small"
                />
                <Chip
                  label={`${selectedDoc.content?.length || 0} chars`}
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Typography variant="subtitle2">Content:</Typography>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', maxHeight: 400, overflow: 'auto' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {selectedDoc.content}
                </Typography>
              </Paper>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Added: {selectedDoc.createdAt ? new Date(selectedDoc.createdAt).toLocaleString() : 'Unknown'}
                {selectedDoc.updatedAt && selectedDoc.updatedAt !== selectedDoc.createdAt && (
                  <> | Updated: {new Date(selectedDoc.updatedAt).toLocaleString()}</>
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              handleEdit(selectedDoc);
            }}
            variant="contained"
          >
            Edit Document
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReferenceDocEditor;
